import role11Schema from '../../../../assets/forms/role-220100.form.json';
import { SSMT, SSMT_SCOPE } from '../../../constants/qnames';

/**
 * Shared business rules for Role 11 (Sub-classification of Assets,
 * Liabilities and Equity, by order of LIQUIDITY).
 *
 * Role 11 is the liquidity-layout cousin of role 9. Where role 9 ships TWO
 * sibling schemas (MFRS / MPERS) and uses bracket-notation flat keys
 * (`qname[Consolidated][CurrentYear]`), role 11 ships a SINGLE schema
 * (`role-220100.form.json`) and uses suffix-style flat keys
 * (`qname + 'GroupCurrent'` etc.) wired by the editor's `buildForm()`. The
 * suffix shape is shared with role 10
 * (`statementOfFinancialPositionLiquidity`) — both sheets are part of the
 * "by order of liquidity" presentation alternative to the
 * current/non-current layout.
 *
 * Both the editor's role11-220100-sub-classification-liquidity.component.ts
 * AND role11SubClassificationLiquidityValidator import from this file so
 * the column-suffix matrix, calculation row groups, and LB-001 fix-guard
 * helpers live in exactly one place.
 *
 * ---- Sheet status posture (mirror of role 13 / role 9 MPERS) ------------
 *
 * The component does NOT call
 * `stateService.updateSheetLevelStatus('subClassificationLiquidity', ...)`
 * anywhere. The only sheet-status writes are
 * `markSheetInitializedWithStatus('subClassificationLiquidity', false)` at
 * mount and after a `loadSavedState()` patch — both of which set
 * `hasErrors: false` unconditionally. There is no `evaluateSheetStatus()`
 * loop; the form has no `Validators.required` on any monetary cell
 * (schema declares `required: false` everywhere), and the only `min`
 * validator wiring in `buildForm()` is gated on
 * `control.validators?.find(v => v.name === 'min')` — which the schema
 * never provides. Net effect: the editor's published `hasErrors` is
 * always `false` for this sheet today.
 *
 * The pure validator therefore returns `hasErrors: false` deterministically
 * but carries the LB-001 fix-guard (required-field detection from BOTH
 * `control.required === true` AND unconditional
 * `validators: [{ name: 'required' }]`) so any future schema edit gets
 * picked up automatically — matching roles 22 / 21 / 13 / 9.
 *
 * ---- Calculations --------------------------------------------------------
 *
 * The component's `calculateTotals()` derives 10 row-sum totals per visible
 * column suffix:
 *
 *   PropertyPlantAndEquipmentSubClassificationLiquidity
 *     = LandAndBuildings + PlantAndMachinery + MotorVehicles
 *     + FurnitureFixturesAndFittings + OfficeEquipment
 *     + CapitalWorkInProgress
 *
 *   IntangibleAssetsSubClassificationLiquidity
 *     = ComputerSoftware + Goodwill + BrandNames + OtherIntangibleAssets
 *
 *   InventoriesSubClassificationLiquidity
 *     = RawMaterials + WorkInProgress + FinishedGoods + ConsumablesAndSpares
 *
 *   TradeReceivablesNetAmountLiquidity
 *     = TradeReceivablesGrossAmount - AllowanceForDoubtfulDebts
 *
 *   OtherReceivablesSubClassificationLiquidity
 *     = Deposits + Prepayments + AccruedIncome
 *
 *   TradePayablesSubClassificationLiquidity
 *     = TradePayablesDomestic + TradePayablesForeign
 *
 *   OtherPayablesSubClassificationLiquidity
 *     = AccruedExpenses + DepositsReceived + SundryPayables
 *
 *   BorrowingsSubClassificationLiquidity
 *     = BankOverdraftsSubClassification + TermLoans + RevolvingCredit
 *     + OtherBorrowings
 *
 *   ShareCapitalSubClassificationLiquidity
 *     = OrdinaryShares + PreferenceShares
 *
 *   OtherReservesSubClassificationLiquidity
 *     = RevaluationSurplus + ForeignCurrencyTranslationReserve
 *     + OtherCapitalReserves
 *
 * Calculated cells are persisted into the same `subClassificationLiquidity`
 * payload via `setValue(..., { emitEvent: false })`, so the pure validator
 * reads post-calculation values directly from `sheetData`. The validator
 * does NOT need to replay the calculations to evaluate `hasErrors`
 * (there are no equality blocks in the schema), but the row-group table
 * is exported here so a future schema edit that introduces an equality
 * block on a calculated total has a single source of truth to consult.
 *
 * ---- Cross-sheet posture ------------------------------------------------
 *
 * Role 11 is registered as Tier 4 in the implementation plan because it
 * shares the dependency-graph position with role 9 (sub-classification ->
 * SOFP). The sheet-dependency map already lists `scopeOfFiling` as a
 * prerequisite of `subClassificationLiquidity` (column-visibility driver),
 * but does NOT list any reverse edge from the liquidity SOFP (role 10)
 * because the role 11 schema declares ZERO `validation.equality` blocks
 * today — the role 10 totals are never compared against role 11
 * subtotals. The validator still reads
 * `context.crossSheetData?.get('statementOfFinancialPositionLiquidity')`
 * defensively (per the Tier 4 task contract) so a future equality edit
 * can plug in without re-threading the interface; today the slice is
 * intentionally unused.
 */

/**
 * scopeOfFiling field whose value drives the Group / Company column
 * visibility. Mirrors the component's `checkConsolidatedStatus()`.
 */
export const ROLE11_CONSOLIDATED_FIELD =
  SSMT_SCOPE.DisclosureOfWhetherFinancialStatementsAreConsolidated;
export const ROLE11_CONSOLIDATED_VALUE = 'Yes';

/**
 * scopeOfFiling field whose value drives the Previous-Year column
 * visibility. Mirrors the component's `checkConsolidatedStatus()`.
 */
export const ROLE11_COMPARATIVE_FIELD =
  SSMT_SCOPE.DisclosureOfComparativeInformation;
export const ROLE11_COMPARATIVE_PRESENTED_VALUES: ReadonlyArray<string> = [
  'Comparative information presented',
  'Restated comparative information',
];

/**
 * Narrow shape of a role 11 schema control used by the pure validator and
 * the editor's calculation registry. The full schema carries far more
 * metadata (ui.placeholder, ui.level, currency, etc.) but the validator
 * only needs qname, type, required, validators, and the calculated/readonly
 * flags — keeping this type tight makes it resilient to unrelated schema
 * additions.
 */
export interface Role11SchemaValidator {
  name?: string;
  value?: number;
  conditionalOn?: unknown;
}

export interface Role11SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Role11SchemaValidator[];
  ui?: {
    control?: string;
    calculated?: boolean;
    readonly?: boolean;
    dualColumn?: boolean;
    level?: string;
    defaultValue?: string;
  };
  validation?: {
    equality?: ReadonlyArray<{
      sheet: string;
      field: string;
      operation?: 'sum' | string;
      tolerance?: number;
      message?: string;
    }>;
  };
}

export interface Role11Schema {
  role: string;
  definition?: string;
  sheetId: string;
  label: string;
  controls: Role11SchemaControl[];
}

/**
 * Resolve the role 11 schema. Single-variant — role 11 does not branch on
 * accounting standard (unlike role 9). Exposed as a function for symmetry
 * with the role-9 / role-13 picker style and so a future MFRS/MPERS split
 * (if it ever happens) only changes this one place.
 */
export function getRole11Schema(): Role11Schema {
  return role11Schema as unknown as Role11Schema;
}

/**
 * Schema controls that participate in validation. Excludes section headers
 * exactly as the component's `buildForm()` does (it `return`s on
 * `type === 'SectionHeader'`). Section headers never become FormControls and
 * therefore cannot fail validation.
 */
export function getRole11ValidatingControls(
  schema: Role11Schema
): ReadonlyArray<Role11SchemaControl> {
  return schema.controls.filter((control) => control.type !== 'SectionHeader');
}

/**
 * Is this control a calculated / readonly total? Calculated cells are
 * populated by the component's `calculateTotals()` after valueChanges
 * settle and are never user-editable, so even if a future schema edit
 * marked one required, the validator should not flag emptiness until the
 * calculation engine has run. Mirrors the component's
 * `isCalculatedField()` plus the `ui.readonly` arm in `buildForm()`'s
 * `disabled` flag computation.
 */
export function isRole11CalculatedControl(
  control: Role11SchemaControl
): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/** Empty-value check matching the component's null/undefined/'' guards. */
export function isRole11Empty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * LB-001 fix-guard: a control is statically required when either
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry. Mirrors the role-6 post-fix check so that
 * any future schema edit to role 11 (none exist today) would be picked up
 * automatically without a separate code change.
 */
export function isRole11StaticallyRequired(
  control: Role11SchemaControl
): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some(
        (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
      )
    : false;
}

/**
 * Visible column suffixes for the current filing. Mirrors exactly the
 * branch ladder in the component's `buildForm()` and `calculateTotals()`.
 *
 *   isConsolidated && hasPreviousYear  →
 *     ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious']
 *   isConsolidated && !hasPreviousYear →
 *     ['GroupCurrent', 'CompanyCurrent']
 *   !isConsolidated && hasPreviousYear →
 *     ['Current', 'Previous']
 *   !isConsolidated && !hasPreviousYear →
 *     ['Current']
 */
export type Role11ColumnSuffix =
  | 'GroupCurrent'
  | 'GroupPrevious'
  | 'CompanyCurrent'
  | 'CompanyPrevious'
  | 'Current'
  | 'Previous';

export function resolveRole11ColumnSuffixes(
  isConsolidated: boolean,
  hasPreviousYear: boolean
): ReadonlyArray<Role11ColumnSuffix> {
  if (isConsolidated && hasPreviousYear) {
    return ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious'];
  }
  if (isConsolidated && !hasPreviousYear) {
    return ['GroupCurrent', 'CompanyCurrent'];
  }
  if (!isConsolidated && hasPreviousYear) {
    return ['Current', 'Previous'];
  }
  return ['Current'];
}

/**
 * Resolve `(isConsolidated, hasPreviousYear)` from a `scopeOfFiling`
 * payload. Mirrors the component's `checkConsolidatedStatus()` exactly so
 * the validator's column-visibility decisions track the editor.
 *
 * When the slice is missing entirely (parity-side mid-init, or a
 * conversion the user has not opened), we default to the conservative
 * "neither flag set" — i.e. single Current column. The component's pre-
 * load state defaults the same way (both fields are `false` initialisers
 * before `checkConsolidatedStatus()` runs), so this matches the editor's
 * pre-mount behaviour.
 */
export function resolveRole11LayoutFlags(
  scopeOfFilingSlice: Record<string, unknown> | undefined
): { isConsolidated: boolean; hasPreviousYear: boolean } {
  if (!scopeOfFilingSlice) {
    return { isConsolidated: false, hasPreviousYear: false };
  }
  const isConsolidated =
    scopeOfFilingSlice[ROLE11_CONSOLIDATED_FIELD] === ROLE11_CONSOLIDATED_VALUE;
  const comparative = scopeOfFilingSlice[ROLE11_COMPARATIVE_FIELD];
  const hasPreviousYear =
    typeof comparative === 'string' &&
    ROLE11_COMPARATIVE_PRESENTED_VALUES.includes(comparative);
  return { isConsolidated, hasPreviousYear };
}

/**
 * Calculation row groups — single source of truth for the 10 row-sum
 * totals the editor's `calculateTotals()` derives. Each entry maps a
 * calculated qname to its contributing input qnames; the optional
 * `subtractFromIndex` marks an input that contributes negatively (used
 * only for `TradeReceivablesNetAmountLiquidity` where allowance is
 * subtracted from gross).
 *
 * The pure validator does not USE this table today — there are no
 * equality blocks to verify — but it is exported so:
 *   • A future equality block on any of these calculated totals can plug
 *     in without duplicating the column expansion.
 *   • If the editor's calculateTotals() is ever refactored to consume
 *     this table, the two paths stay in lockstep.
 */
export interface Role11CalculationGroup {
  /** qname of the calculated total cell. */
  total: string;
  /**
   * Contributing input qnames. All entries contribute positively except
   * the index named in `subtractFromIndex`, which contributes negatively.
   */
  inputs: ReadonlyArray<string>;
  /** Index into `inputs` of the negative contributor (TradeReceivables only). */
  subtractFromIndex?: number;
}

export const ROLE11_CALCULATION_GROUPS: ReadonlyArray<Role11CalculationGroup> = [
  {
    total: SSMT.PropertyPlantAndEquipmentSubClassificationLiquidity,
    inputs: [
      SSMT.LandAndBuildingsLiquidity,
      SSMT.PlantAndMachineryLiquidity,
      SSMT.MotorVehiclesLiquidity,
      SSMT.FurnitureFixturesAndFittingsLiquidity,
      SSMT.OfficeEquipmentLiquidity,
      SSMT.CapitalWorkInProgressLiquidity,
    ],
  },
  {
    total: SSMT.IntangibleAssetsSubClassificationLiquidity,
    inputs: [
      SSMT.ComputerSoftwareLiquidity,
      SSMT.GoodwillLiquidity,
      SSMT.BrandNamesLiquidity,
      SSMT.OtherIntangibleAssetsLiquidity,
    ],
  },
  {
    total: SSMT.InventoriesSubClassificationLiquidity,
    inputs: [
      SSMT.RawMaterialsLiquidity,
      SSMT.WorkInProgressLiquidity,
      SSMT.FinishedGoodsLiquidity,
      SSMT.ConsumablesAndSparesLiquidity,
    ],
  },
  {
    total: SSMT.TradeReceivablesNetAmountLiquidity,
    inputs: [
      SSMT.TradeReceivablesGrossAmountLiquidity,
      SSMT.AllowanceForDoubtfulDebtsLiquidity,
    ],
    subtractFromIndex: 1,
  },
  {
    total: SSMT.OtherReceivablesSubClassificationLiquidity,
    inputs: [
      SSMT.DepositsLiquidity,
      SSMT.PrepaymentsLiquidity,
      SSMT.AccruedIncomeLiquidity,
    ],
  },
  {
    total: SSMT.TradePayablesSubClassificationLiquidity,
    inputs: [
      SSMT.TradePayablesDomesticLiquidity,
      SSMT.TradePayablesForeignLiquidity,
    ],
  },
  {
    total: SSMT.OtherPayablesSubClassificationLiquidity,
    inputs: [
      SSMT.AccruedExpensesLiquidity,
      SSMT.DepositsReceivedLiquidity,
      SSMT.SundryPayablesLiquidity,
    ],
  },
  {
    total: SSMT.BorrowingsSubClassificationLiquidity,
    inputs: [
      SSMT.BankOverdraftsSubClassificationLiquidity,
      SSMT.TermLoansLiquidity,
      SSMT.RevolvingCreditLiquidity,
      SSMT.OtherBorrowingsLiquidity,
    ],
  },
  {
    total: SSMT.ShareCapitalSubClassificationLiquidity,
    inputs: [
      SSMT.OrdinarySharesLiquidity,
      SSMT.PreferenceSharesLiquidity,
    ],
  },
  {
    total: SSMT.OtherReservesSubClassificationLiquidity,
    inputs: [
      SSMT.RevaluationSurplusLiquidity,
      SSMT.ForeignCurrencyTranslationReserveLiquidity,
      SSMT.OtherCapitalReservesLiquidity,
    ],
  },
];

/**
 * Build the flat key the editor stores its column values under
 * (`qname + suffix`). Used by the validator for sheetData reads and
 * available to the component if it ever consumes this helper directly.
 */
export function buildRole11FlatKey(
  qname: string,
  suffix: Role11ColumnSuffix
): string {
  return `${qname}${suffix}`;
}
